import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { CheckCircle, X, RefreshCw, ShoppingCart, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// DJ Course - One-time purchase (includes lifetime Starter membership)
const DJ_COURSE = {
  id: 'course',
  name: '30-Day Beginner DJ Course',
  price: '£45.99',
  type: 'one-time',
  features: [
    'Complete 30-day structured programme',
    'Learn Serato DJ from scratch',
    'DJ Equipment & Setup guide',
    'Music Organisation techniques',
    'Beatmatching & EQ fundamentals',
    'Creative Mixing Tools',
    'Transition techniques',
    'Stems & Advanced Tools',
    'Reading the Crowd',
    'Microphone Confidence training',
    'Building Your DJ Brand',
    'Promotion & Networking strategies',
    'Beginner Scratch Techniques',
    'Practice routines & exercises',
    'Certificate of completion',
  ],
  bonusFeatures: [
    'Lifetime access to Blendonics Starter Membership',
    'Access to DJ resource library',
    'Blendonics DJ community access',
  ],
};

// Membership Tiers - Recurring monthly subscriptions
const TIERS = [
  {
    id: 'starter',
    name: 'Blendonics Starter',
    price: '£12.99',
    period: '/month',
    type: 'subscription',
    features: [
      'Full access to 30-Day Beginner DJ Course',
      'Essential DJ resource library',
      'PDFs, quizzes, music resources',
      'DJ tools and templates',
      'Member-only updates',
      'Blendonics DJ community access',
    ],
    excluded: [
      'Premium content library',
      'Exclusive eBooks & SFX packs',
      'Advanced tutorials',
      '1:1 coaching calls',
    ],
  },
  {
    id: 'pro',
    name: 'Blendonics Pro',
    price: '£25.99',
    period: '/month',
    type: 'subscription',
    popular: true,
    features: [
      'Everything in Starter, plus:',
      'Full premium content library',
      'Exclusive eBooks',
      'Blendonics SFX sample packs',
      'Monthly skill challenges',
      'Access to all future online courses',
    ],
    excluded: [
      'Monthly 1:1 coaching call',
      'Personal DJ improvement plan',
      'Mix upload & feedback',
      '24/7 guidance',
    ],
  },
  {
    id: 'elite',
    name: 'Blendonics ELITE',
    price: '£79.99',
    period: '/month',
    type: 'subscription',
    bestValue: true,
    features: [
      'Everything in Pro, plus:',
      'Monthly 1:1 DJ coaching call (15-20 mins)',
      'Personal DJ improvement plan',
      'Mix upload with detailed feedback',
      'Direct messaging with pro DJ',
      '24/7 guidance',
      'Priority support',
    ],
    excluded: [],
  },
];

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const courseRef = useRef(null);
  const [loading, setLoading] = useState({});
  const [showConsumerRights, setShowConsumerRights] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null);
  const [checkingReferral, setCheckingReferral] = useState(false);
  const [showSubscriberWarning, setShowSubscriberWarning] = useState(false);

  // Check if user has active subscription
  const hasActiveSubscription = user?.membership_status === 'active' && user?.stripe_subscription_id;
  const currentTier = user?.membership_tier;

  // Handle scroll to course section when coming from landing page
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('purchase') === 'course' && courseRef.current) {
      setTimeout(() => {
        courseRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Add a highlight effect
        courseRef.current.classList.add('ring-4', 'ring-yellow-500/50');
        setTimeout(() => {
          courseRef.current.classList.remove('ring-4', 'ring-yellow-500/50');
        }, 2000);
      }, 500);
    }
  }, [location]);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 5) {
      setReferralValid(null);
      return;
    }
    
    setCheckingReferral(true);
    try {
      const response = await axios.get(`${API}/referral/code/${code}`);
      if (response.data.valid) {
        setReferralValid(true);
        toast.success('Referral code applied! 10% discount will be applied at checkout.');
      }
    } catch (error) {
      setReferralValid(false);
      toast.error('Invalid referral code');
    }
    setCheckingReferral(false);
  };

  const handlePurchaseClick = async (packageType, price) => {
    if (!user) {
      toast.error('Please login to continue');
      navigate('/login');
      return;
    }
    
    // For one-time course purchase
    if (packageType === 'course') {
      // Check if user entered a referral code but didn't apply it
      if (referralCode && referralValid === null) {
        toast.error('Please apply your discount code before clicking "Buy Now"');
        return;
      }
      
      // Scenario 3: If user has active subscription, show warning modal
      if (hasActiveSubscription) {
        setShowSubscriberWarning(true);
        return;
      }
      
      // Proceed with course purchase
      proceedWithCoursePurchase();
      return;
    }
    
    // For recurring subscriptions, show consumer rights popup
    setSelectedTier({ type: packageType, price });
    setShowConsumerRights(true);
  };

  const proceedWithCoursePurchase = async () => {
    setShowSubscriberWarning(false);
    setLoading({ ...loading, course: true });
    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        package_type: 'course',
        origin_url: window.location.origin || window.location.href.split('/').slice(0, 3).join('/'),
        referral_code: referralValid ? referralCode : null,
      });
      window.location.href = response.data.url;
    } catch (error) {
      // Handle authentication errors specifically
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Your session has expired. Please log in again.');
        navigate('/login');
        return;
      }
      
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'object') {
        toast.error('Failed to create checkout. Please try again.');
      } else {
        toast.error(errorMsg || 'Failed to create checkout. Please try again.');
      }
      setLoading({ ...loading, course: false });
    }
  };

  const confirmPurchase = async () => {
    if (!selectedTier) return;
    
    setShowConsumerRights(false);
    setLoading({ ...loading, [selectedTier.type]: true });
    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        package_type: selectedTier.type,
        origin_url: window.location.origin || window.location.href.split('/').slice(0, 3).join('/'),
      });
      window.location.href = response.data.url;
    } catch (error) {
      // Handle authentication errors specifically
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Your session has expired. Please log in again.');
        navigate('/login');
        return;
      }
      
      toast.error(error.response?.data?.detail || 'Failed to create checkout. Please try again.');
      setLoading({ ...loading, [selectedTier.type]: false });
    }
  };

  return (
    <div className="min-h-screen py-24" data-testid="pricing-page" style={{ forcedColorAdjust: 'none' }}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="pricing-title" style={{ color: '#ffffff' }}>
            Memberships and Pricing
          </h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#a3a3a3' }}>
            Choose from our one-time DJ course or monthly membership subscriptions to unlock your DJ potential.
          </p>
        </div>

        {/* DJ Course - One-time Purchase */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-center mb-8" style={{ color: '#ffffff' }}>
            <ShoppingCart className="inline mr-2" size={24} style={{ color: '#D4AF37' }} />
            One-Time Purchase
          </h2>
          <div className="max-w-2xl mx-auto">
            <div 
              ref={courseRef}
              className="rounded-3xl border-2 bg-card p-8 shadow-xl transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-yellow-500/30"
              style={{ borderColor: '#D4AF37', boxShadow: '0 0 30px rgba(212, 175, 55, 0.3)' }}
              data-testid="pricing-tier-course"
            >
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#ffffff' }}>{DJ_COURSE.name}</h3>
                <div className="flex items-baseline justify-center">
                  <span className="text-4xl font-bold font-mono" style={{ color: '#D4AF37' }}>{DJ_COURSE.price}</span>
                  <span className="ml-2 text-sm" style={{ color: '#a3a3a3' }}>one-time payment</span>
                </div>
              </div>
              
              {/* Course Features */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mb-6">
                {DJ_COURSE.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start">
                    <CheckCircle className="mr-2 mt-0.5 flex-shrink-0" size={18} style={{ color: '#D4AF37' }} />
                    <span className="text-sm" style={{ color: '#ffffff' }}>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Bonus - Lifetime Starter Membership */}
              <div className="rounded-xl p-4 mb-6" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
                <p className="text-center text-sm font-semibold mb-3" style={{ color: '#D4AF37' }}>
                  🎁 BONUS: Includes Lifetime Access
                </p>
                <div className="space-y-2">
                  {DJ_COURSE.bonusFeatures.map((feature, idx) => (
                    <div key={idx} className="flex items-center justify-center">
                      <CheckCircle className="mr-2 flex-shrink-0" size={16} style={{ color: '#22c55e' }} />
                      <span className="text-sm" style={{ color: '#ffffff' }}>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Referral Code Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: '#a3a3a3' }}>
                  Have a referral code? Get 10% off!
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={referralCode}
                    onChange={(e) => {
                      setReferralCode(e.target.value.toUpperCase());
                      setReferralValid(null);
                    }}
                    placeholder="Enter referral code"
                    className="flex-1 px-4 py-2 rounded-full border bg-background text-foreground"
                    style={{ borderColor: referralValid === true ? '#22c55e' : referralValid === false ? '#ef4444' : '#333' }}
                    data-testid="referral-code-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => validateReferralCode(referralCode)}
                    disabled={checkingReferral || !referralCode}
                    className="rounded-full px-4"
                    data-testid="apply-referral-button"
                  >
                    {checkingReferral ? '...' : 'Apply'}
                  </Button>
                </div>
                {referralValid === true && (
                  <p className="text-sm mt-2 flex items-center" style={{ color: '#22c55e' }}>
                    <CheckCircle size={14} className="mr-1" /> 10% discount will be applied at checkout
                  </p>
                )}
                {referralValid === false && (
                  <p className="text-sm mt-2 flex items-center" style={{ color: '#ef4444' }}>
                    <X size={14} className="mr-1" /> Invalid referral code
                  </p>
                )}
              </div>

              <Button
                className="w-full rounded-full h-12 text-lg font-semibold"
                onClick={() => handlePurchaseClick('course', DJ_COURSE.price)}
                disabled={loading.course}
                style={{ background: '#D4AF37', color: '#141414' }}
                data-testid="buy-course-button"
              >
                {loading.course ? 'Processing...' : 'Buy Now - Get Lifetime Access'}
              </Button>
            </div>
          </div>
        </div>

        {/* Membership Tiers - Recurring Subscriptions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-center mb-2" style={{ color: '#ffffff' }}>
            <RefreshCw className="inline mr-2" size={24} style={{ color: '#D4AF37' }} />
            Monthly Memberships
          </h2>
          <p className="text-center mb-8" style={{ color: '#a3a3a3' }}>
            Recurring monthly subscriptions • Cancel anytime
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {TIERS.map((tier) => (
            <div
              key={tier.id}
              className={`flex flex-col rounded-3xl border-2 bg-card p-8 shadow-xl relative transition-all duration-300 hover:scale-110 hover:z-10 ${
                tier.popular ? 'scale-105 hover:shadow-yellow-500/40' : tier.bestValue ? 'scale-105 hover:shadow-purple-500/40' : 'hover:shadow-yellow-500/30'
              } hover:shadow-2xl`}
              style={{
                borderColor: tier.popular ? '#D4AF37' : tier.bestValue ? '#3a0088' : '#262626',
                boxShadow: tier.popular ? '0 0 30px rgba(212, 175, 55, 0.5)' : tier.bestValue ? '0 0 30px rgba(58, 0, 136, 0.5)' : undefined,
                forcedColorAdjust: 'none'
              }}
              data-testid={`pricing-tier-${tier.id}`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-sm font-semibold" style={{ background: '#D4AF37', color: '#141414' }}>
                  Most Popular
                </div>
              )}
              {tier.bestValue && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-sm font-semibold" style={{ background: '#3a0088', color: '#ffffff' }}>
                  Best Value
                </div>
              )}
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#ffffff' }}>{tier.name}</h3>
                <div className="flex items-baseline">
                  <span className="text-4xl font-bold font-mono" style={{ color: '#ffffff' }}>{tier.price}</span>
                  <span className="ml-2" style={{ color: '#a3a3a3' }}>{tier.period}</span>
                </div>
              </div>
              <div className="space-y-3 mb-8 flex-grow">
                {tier.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start" data-testid={`feature-${tier.id}-${idx}`}>
                    <CheckCircle className="mr-2 mt-0.5 flex-shrink-0" size={20} style={{ color: '#D4AF37' }} />
                    <span className="text-sm" style={{ color: '#ffffff' }}>{feature}</span>
                  </div>
                ))}
                {tier.excluded.map((feature, idx) => (
                  <div key={idx} className="flex items-start opacity-40">
                    <X className="mr-2 mt-0.5 flex-shrink-0" size={20} style={{ color: '#a3a3a3' }} />
                    <span className="text-sm line-through" style={{ color: '#a3a3a3' }}>{feature}</span>
                  </div>
                ))}
              </div>
              <Button
                className="w-full rounded-full"
                size="lg"
                onClick={() => handlePurchaseClick(tier.id, tier.price)}
                disabled={loading[tier.id] || (user?.membership_tier === tier.id)}
                style={{
                  background: tier.popular ? '#D4AF37' : '#262626',
                  color: tier.popular ? '#141414' : '#ffffff',
                  border: tier.popular ? 'none' : '2px solid #262626',
                  fontWeight: tier.popular ? '600' : '400'
                }}
                data-testid={`purchase-button-${tier.id}`}
              >
                {user?.membership_tier === tier.id
                  ? 'Current Plan'
                  : loading[tier.id]
                  ? 'Processing...'
                  : 'Get Started'}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {showConsumerRights && selectedTier && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowConsumerRights(false)}>
          <div className="glass-panel rounded-2xl p-8 max-w-md w-full border-2" style={{ borderColor: '#D4AF37' }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-2xl font-bold mb-4">Consumer Rights</h3>
            <p className="text-sm mb-6 leading-relaxed" style={{ color: '#a3a3a3' }}>
              By subscribing, you agree to a recurring charge of {selectedTier.price} per month, billed automatically. You can cancel at any time before your next billing date, and your access will continue until the end of the current billing period.
            </p>
            <div className="flex gap-4">
              <Button
                onClick={() => setShowConsumerRights(false)}
                variant="outline"
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmPurchase}
                className="flex-1 rounded-full transition-all duration-300"
                style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}
              >
                Agree & Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Subscriber Warning Modal - Scenario 3: Paid subscriber buying course */}
      {showSubscriberWarning && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowSubscriberWarning(false)} data-testid="subscriber-warning-modal">
          <div className="glass-panel rounded-2xl p-8 max-w-md w-full border-2" style={{ borderColor: '#ef4444' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={28} style={{ color: '#ef4444' }} />
              <h3 className="text-2xl font-bold" style={{ color: '#ffffff' }}>Important Notice</h3>
            </div>
            <div className="space-y-4 mb-6">
              <p className="text-sm leading-relaxed" style={{ color: '#a3a3a3' }}>
                You currently have an <strong style={{ color: '#D4AF37' }}>active {currentTier?.charAt(0).toUpperCase() + currentTier?.slice(1)} subscription</strong>.
              </p>
              <div className="p-4 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                <p className="text-sm" style={{ color: '#fca5a5' }}>
                  <strong>By purchasing the course:</strong>
                </p>
                <ul className="text-sm mt-2 space-y-1" style={{ color: '#fca5a5' }}>
                  <li>• Your current subscription will be <strong>cancelled immediately</strong></li>
                  <li>• You will be converted to <strong>Lifetime Starter</strong> status</li>
                  <li>• You will lose access to {currentTier === 'elite' ? 'Elite features (coaching, etc.)' : currentTier === 'pro' ? 'Pro features (eBooks, SFX packs, etc.)' : 'your current tier features'}</li>
                </ul>
              </div>
              <div className="p-4 rounded-lg" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                <p className="text-sm" style={{ color: '#86efac' }}>
                  <strong>What you&apos;ll get:</strong>
                </p>
                <ul className="text-sm mt-2 space-y-1" style={{ color: '#86efac' }}>
                  <li>• Lifetime access to the 30-Day DJ Course</li>
                  <li>• Permanent Starter membership (no monthly fees)</li>
                  <li>• Access to community and basic resources</li>
                </ul>
              </div>
            </div>
            <div className="flex gap-4">
              <Button
                onClick={() => setShowSubscriberWarning(false)}
                variant="outline"
                className="flex-1 rounded-full"
                data-testid="cancel-warning-btn"
              >
                Keep Subscription
              </Button>
              <Button
                onClick={proceedWithCoursePurchase}
                className="flex-1 rounded-full transition-all duration-300"
                style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}
                data-testid="proceed-course-purchase-btn"
              >
                Buy Course Anyway
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;